import { ISTANBUL_TZ, istanbulDayString } from './time';

/**
 * Bildirim ortak sözleşmesi.
 *
 * Tasarım: `docs/entegrasyon/.../2026-10-12-bildirim-altyapisi.md` §3 (bağlayıcı).
 * Paketler (P1–P5) bu sözleşmeye yazılır; değişmez. Yeni kind'lar buradaki sözlüğe
 * eklenir; geriye dönük ad değişikliği YOKTUR (eski satırlar + panel + testler).
 */

// ──────────────────────────────────────────────────────────────────────────────
// Kind sözlüğü
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Mevcut + yeni bildirim türleri. Mevcut olanlar (büyük harfli sabitler dâhil)
 * eski satırlarla aynı kalır — panel ve testler bu adlara bağlı.
 *
 * Yeni kind'lar `kategori.olay` biçiminde; örn. `order.assigned`, `trip.loaded`.
 */
export const NOTIFICATION_KIND = {
  // ── Mevcut (yeniden adlandırılmaz) ──
  ORDER_RECEIVED: 'order.received',
  ORDER_TRANSFER_REQUESTED: 'order.transfer.requested',
  ORDER_TRANSFER_REJECTED: 'order.transfer.rejected',
  ORDER_TRANSFER_APPROVED: 'order.transfer.approved',
  // Hayat / Abant gözlemi atanmış ya da yoldaki siparişimizle çelişiyor.
  // orders.service.ts HAYAT_CLOSED_KIND / ABANT_CLOSED_KIND ile bire bir eşleşir.
  HAYAT_CLOSED_EXTERNALLY: 'hayat.closedExternally',
  ABANT_CLOSED_EXTERNALLY: 'abant.closedExternally',
  RELOAD_REQUEST: 'RELOAD_REQUEST',
  TRIP_VARIANCE: 'TRIP_VARIANCE',
  DELIVERY_PHOTO_MISSING: 'DELIVERY_PHOTO_MISSING',
  TRIP_VOIDED: 'TRIP_VOIDED',
  DELIVERY_DEVIATION: 'DELIVERY_DEVIATION',
  DELIVERY_DEVIATION_REVIEWED: 'delivery.deviation.reviewed',
  DELIVERY_DEVIATION_REJECTED: 'delivery.deviation.rejected',
  // Sipariş zaten teslimken düşen mükerrer teslim (farklı sefer/şoför); nakit izi.
  DELIVERY_DROPPED_DUPLICATE: 'delivery.droppedDuplicate',
  STOCK_COUNT_DIFF: 'STOCK_COUNT_DIFF',
  PIN_RESET_REQUEST: 'PIN_RESET_REQUEST',
  PRICE_SYNC: 'price-sync',
  LATE_ORDER_WINDOW: 'late-order-window',

  // ── Yeni: yöneticilere (stok) ──
  STOCK_SHIPMENT_RECEIVED: 'stock.shipment.received',
  STOCK_ADJUSTED: 'stock.adjusted',
  STOCK_FOREIGN_RECEIVED: 'stock.foreign.received',
  STOCK_FOREIGN_SETTLED: 'stock.foreign.settled',
  // S6: Akşam depo sayımı yapılmadıysa patrona hatırlatma. `targetId` = İstanbul günü;
  // günde TEK bildirim (depo sayıldıysa hiç yazılmaz).
  STOCK_COUNT_REMINDER_DEPOT: 'stock.count.reminder.depot',

  // ── Yeni: yöneticilere (sefer) ──
  TRIP_LOADED: 'trip.loaded',
  TRIP_CLOSED: 'trip.closed',

  // ── Yeni: yöneticilere (rapor) ──
  REPORT_DAILY_READY: 'report.daily.ready',
  // Pazartesi 08:00 (Europe/Istanbul) haftalık karne — `NotificationsService.notify`
  // `userIds` ile çağrılır, yalnızca OWNER kullanıcılarına gider (daily-report.taslak).
  REPORT_WEEKLY_SCORECARD: 'report.weekly.scorecard',
  // Her gün 08:00 (Europe/Istanbul) sistem sağlık raporu: entegrasyon çekimleri, ölü işler, yedek.
  REPORT_HEALTH_DAILY: 'report.health.daily',

  // ── Yeni: yöneticilere (Hayat entegrasyon uyarıları) ──
  // @deprecated Y1b: üretici yok; yeni yol CHANNEL_WRITE_ALERT. (Eski satırlar ve mobil eşleme için KALIR.)
  HAYAT_OBSERVED_CONFLICT: 'hayat.observedConflict',
  // @deprecated Y1b: üretici yok; yeni yol CHANNEL_WRITE_ALERT
  HAYAT_RETRY_LIMIT: 'hayat.retryLimit',
  // @deprecated Y1b: üretici yok; yeni yol CHANNEL_WRITE_ALERT
  HAYAT_PUSH_SENT: 'hayat.pushSent',
  // @deprecated Y1b: üretici yok; yeni yol CHANNEL_WRITE_ALERT
  HAYAT_PUSH_FAILED: 'hayat.pushFailed',
  // Kanala geri yazma uyarısı (Getir oturumu reddedildi, kanal çakışması…). Yalnız yöneticilere.
  CHANNEL_WRITE_ALERT: 'channelWrite.alert',

  // ── Yeni: şoföre ──
  ORDER_ASSIGNED: 'order.assigned',
  ORDER_UNASSIGNED: 'order.unassigned',
  // Yöneticiye: araca sipariş atandı ama aracın şoförü yok. targetId = `${vehicleId}:${gün}`.
  ORDER_ASSIGNED_UNSTAFFED: 'order.assigned.unstaffed',
  ORDER_CANCELLED: 'order.cancelled',
  ORDER_CLOSED_EXTERNALLY: 'order.closedExternally',
  RELOAD_APPROVED: 'reload.approved',
  RELOAD_REJECTED: 'reload.rejected',
  CASH_HANDOVER_CONFIRMED: 'cash.handover.confirmed',

  // ── Yeni: yöneticilere (şoförden şablon/serbest mesaj) ──
  OWNER_MESSAGE: 'owner.message',

  // ── Yeni: personel ──
  // Telefonu girilmemiş personel (telefonla giriş yapamaz); daha önce "system"e düşüyordu.
  STAFF_PHONE_MISSING: 'staff.phoneMissing',

  // ── Yeni: sessiz saat sabah özeti (alıcı başına tek satır, kişisel veri yok) ──
  DIGEST_MORNING: 'digest.morning',

  // ── Yeni: kanal oturumu (elle bağlanan panel oturumu, ADR 2026-10-10) ──
  // Oturumun süresi dolmak üzere / doldu. `targetId` = `${connectionId}:${expiresAt}` —
  // oturum başına BİR kez yazılır (tekilleştirme bu satırın kendi kontrolündedir,
  // DEDUPE_KINDS'e EKLENMEZ).
  CHANNEL_SESSION_EXPIRING: 'channel.session.expiring',
  CHANNEL_SESSION_EXPIRED: 'channel.session.expired',

  // ── Sözlükte OLMAYAN kind'ler (S-N envanteri §2) ──
  // Bu satırlar üreticilerde STRING LITERAL olarak yazılıydı; sözlükte olmadıkları için
  // istemciler bu türleri "bilinmeyen" sayıyordu. Değerler BİREBİR üreticiyle aynıdır —
  // üretici dosyalarına bu turda DOKUNULMAZ (davranış değişikliği yok).
  // `kindCategory()` DAVRANIŞI DEĞİŞMEZ: bu kind'ler `switch`'e eklenmediği için önek
  // kuralına takılır ve `system` kategorisinde kalır (eskiden de öyleydi).
  DRIVER_MESSAGE: 'driver.message',
  GETIR_CLOSED_ORDERS_OVERFLOW: 'getir.closedOrdersOverflow',
  GETIR_CLOSED_EXTERNALLY: 'getir.closedExternally',
  HAYAT_CANCELLED_AFTER_CLOSE: 'hayat.cancelledAfterClose',
  ABANT_CANCELLED_AFTER_CLOSE: 'abant.cancelledAfterClose',
  DEVICE_IN_USE_ATTEMPT: 'DEVICE_IN_USE_ATTEMPT',
  DEVICE_LOGIN_LOCKED: 'DEVICE_LOGIN_LOCKED',
  DEVICE_AUTO_APPROVED: 'DEVICE_AUTO_APPROVED',
  DEVICE_PAIRING_REQUESTED: 'DEVICE_PAIRING_REQUESTED',
  DRIVER_VEHICLE_CHANGED: 'DRIVER_VEHICLE_CHANGED',
  TRIP_TAKEOVER_REQUESTED: 'TRIP_TAKEOVER_REQUESTED',

  // ── Test bildirimi (panel/mobil "kendime test bildirimi gönder") ──
  SYSTEM_TEST: 'system.test',
} as const;

export type NotificationKind = (typeof NOTIFICATION_KIND)[keyof typeof NOTIFICATION_KIND];

/**
 * `Notification.targetType` (mobil/panel gezinme anahtarı). Bilinmeyen tür yalnız
 * listede kalır — hata fırlatmaz, gezinmez.
 */
export const NOTIFICATION_TARGET = {
  ORDER: 'order',
  TRIP: 'trip',
  REPORT: 'report',
  STOCK: 'stock',
  STOCK_COUNT: 'stockCount',
  DELIVERY: 'delivery',
  USER: 'user',
  INTEGRATION: 'integration',
  DRIVER: 'driver',
  /** Sabah özeti: istemci için bilinmeyen tür — yalnız listede kalır, gezinme yok. */
  DIGEST: 'digest',
  /** Kanal panelinden gelen (kendi siparişimiz olmayan) dış sipariş. */
  EXTERNAL_ORDER: 'external-order',
  /** Kanal bağlantısı (Getir/Hayat/Abant oturumu). */
  CHANNEL_CONNECTION: 'channel-connection',
  /** Cihaz (eşleşme, oturum kilidi). */
  DEVICE: 'device',
} as const;

export type NotificationTarget = (typeof NOTIFICATION_TARGET)[keyof typeof NOTIFICATION_TARGET];

// ──────────────────────────────────────────────────────────────────────────────
// Tür sözleşmesi (S-N) — istemciye giden sadeleştirilmiş sınıflandırma
// ──────────────────────────────────────────────────────────────────────────────

/**
 * İstemciye giden bildirim TÜRÜ (14 tür). `kind` satırda ince taneli kalır (dedupe
 * `kind+targetId` üzerine kurulu); sadeleştirme yalnız SÖZLEŞME katmanındadır: API her
 * satıra `type` ve `tier` ekler, istemci yalnız bunları okur.
 *
 * Böylece kind→kategori eşlemesi istemci tarafında ikinci kez yazılmaz (panel ve mobil
 * sözlükleri bugün ayrışıyor). Bilinmeyen kind → `system`.
 */
export const NOTIFICATION_TYPE = {
  ORDER_NEW: 'order.new',
  ORDER_ASSIGNED: 'order.assigned',
  ORDER_REMOVED: 'order.removed',
  APPROVAL_REQUEST: 'approval.request',
  APPROVAL_RESULT: 'approval.result',
  TRIP_ALERT: 'trip.alert',
  TRIP_STATUS: 'trip.status',
  STOCK: 'stock',
  CHANNEL_SYNC: 'channel.sync',
  CHANNEL_SESSION: 'channel.session',
  SECURITY: 'security',
  MESSAGE: 'message',
  REPORT: 'report',
  SYSTEM: 'system',
} as const;

export type NotificationType = (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE];

/**
 * `kind` → `type`. AÇIK tablo: önek tahmini YAPILMAZ — `order.*` öneki `order.new`
 * ile `order.removed` arasında ayrım taşıdığı için önek kuralı burada işe yaramaz.
 * Yeni kind eklenince buraya da eklenir; tabloda olmayan kind `system`'e düşer.
 *
 * (Tasarım: `.claude/agent-memory-local/sn-tasarim.md` Karar 1 tablosu.)
 */
export function kindType(kind: string): NotificationType {
  switch (kind) {
    // ── Sipariş ──
    case NOTIFICATION_KIND.ORDER_RECEIVED:
      return NOTIFICATION_TYPE.ORDER_NEW;
    case NOTIFICATION_KIND.ORDER_ASSIGNED:
      return NOTIFICATION_TYPE.ORDER_ASSIGNED;
    case NOTIFICATION_KIND.ORDER_UNASSIGNED:
    case NOTIFICATION_KIND.ORDER_CANCELLED:
    case NOTIFICATION_KIND.ORDER_CLOSED_EXTERNALLY:
      return NOTIFICATION_TYPE.ORDER_REMOVED;
    // Yönetici uyarısı (şoförsüz araç): şoförün "yeni iş" türüne karışmaz.
    case NOTIFICATION_KIND.ORDER_ASSIGNED_UNSTAFFED:
      return NOTIFICATION_TYPE.TRIP_ALERT;

    // ── Onay: talep ──
    case NOTIFICATION_KIND.ORDER_TRANSFER_REQUESTED:
    case NOTIFICATION_KIND.RELOAD_REQUEST:
    case NOTIFICATION_KIND.DELIVERY_DEVIATION:
    case NOTIFICATION_KIND.PIN_RESET_REQUEST:
    case NOTIFICATION_KIND.DEVICE_PAIRING_REQUESTED:
    case NOTIFICATION_KIND.TRIP_TAKEOVER_REQUESTED:
      return NOTIFICATION_TYPE.APPROVAL_REQUEST;

    // ── Onay: sonuç ──
    case NOTIFICATION_KIND.ORDER_TRANSFER_APPROVED:
    case NOTIFICATION_KIND.ORDER_TRANSFER_REJECTED:
    case NOTIFICATION_KIND.RELOAD_APPROVED:
    case NOTIFICATION_KIND.RELOAD_REJECTED:
    case NOTIFICATION_KIND.DELIVERY_DEVIATION_REVIEWED:
    case NOTIFICATION_KIND.DELIVERY_DEVIATION_REJECTED:
    case NOTIFICATION_KIND.CASH_HANDOVER_CONFIRMED:
      return NOTIFICATION_TYPE.APPROVAL_RESULT;

    // ── Sefer: uyarı ──
    case NOTIFICATION_KIND.TRIP_VARIANCE:
    case NOTIFICATION_KIND.TRIP_VOIDED:
    case NOTIFICATION_KIND.DELIVERY_PHOTO_MISSING:
    case NOTIFICATION_KIND.DELIVERY_DROPPED_DUPLICATE:
      return NOTIFICATION_TYPE.TRIP_ALERT;

    // ── Sefer: durum ──
    case NOTIFICATION_KIND.TRIP_LOADED:
    case NOTIFICATION_KIND.TRIP_CLOSED:
    case NOTIFICATION_KIND.DRIVER_VEHICLE_CHANGED:
      return NOTIFICATION_TYPE.TRIP_STATUS;

    // ── Stok ──
    case NOTIFICATION_KIND.STOCK_SHIPMENT_RECEIVED:
    case NOTIFICATION_KIND.STOCK_ADJUSTED:
    case NOTIFICATION_KIND.STOCK_FOREIGN_RECEIVED:
    case NOTIFICATION_KIND.STOCK_FOREIGN_SETTLED:
    case NOTIFICATION_KIND.STOCK_COUNT_DIFF:
    case NOTIFICATION_KIND.STOCK_COUNT_REMINDER_DEPOT:
      return NOTIFICATION_TYPE.STOCK;

    // ── Kanal senkronizasyonu ──
    case NOTIFICATION_KIND.HAYAT_CLOSED_EXTERNALLY:
    case NOTIFICATION_KIND.ABANT_CLOSED_EXTERNALLY:
    case NOTIFICATION_KIND.GETIR_CLOSED_EXTERNALLY:
    case NOTIFICATION_KIND.HAYAT_CANCELLED_AFTER_CLOSE:
    case NOTIFICATION_KIND.ABANT_CANCELLED_AFTER_CLOSE:
    case NOTIFICATION_KIND.HAYAT_OBSERVED_CONFLICT:
    case NOTIFICATION_KIND.HAYAT_RETRY_LIMIT:
    case NOTIFICATION_KIND.HAYAT_PUSH_SENT:
    case NOTIFICATION_KIND.HAYAT_PUSH_FAILED:
    case NOTIFICATION_KIND.CHANNEL_WRITE_ALERT:
    case NOTIFICATION_KIND.GETIR_CLOSED_ORDERS_OVERFLOW:
    case NOTIFICATION_KIND.LATE_ORDER_WINDOW:
    case NOTIFICATION_KIND.PRICE_SYNC:
      return NOTIFICATION_TYPE.CHANNEL_SYNC;

    // ── Kanal oturumu ──
    case NOTIFICATION_KIND.CHANNEL_SESSION_EXPIRING:
    case NOTIFICATION_KIND.CHANNEL_SESSION_EXPIRED:
      return NOTIFICATION_TYPE.CHANNEL_SESSION;

    // ── Güvenlik ──
    case NOTIFICATION_KIND.DEVICE_IN_USE_ATTEMPT:
    case NOTIFICATION_KIND.DEVICE_LOGIN_LOCKED:
    case NOTIFICATION_KIND.DEVICE_AUTO_APPROVED:
      return NOTIFICATION_TYPE.SECURITY;

    // ── Kişiden kişiye mesaj ──
    case NOTIFICATION_KIND.DRIVER_MESSAGE:
    case NOTIFICATION_KIND.OWNER_MESSAGE:
      return NOTIFICATION_TYPE.MESSAGE;

    // ── Rapor ──
    case NOTIFICATION_KIND.REPORT_DAILY_READY:
    case NOTIFICATION_KIND.REPORT_WEEKLY_SCORECARD:
    case NOTIFICATION_KIND.REPORT_HEALTH_DAILY:
    case NOTIFICATION_KIND.DIGEST_MORNING:
      return NOTIFICATION_TYPE.REPORT;

    // ── Sistem ──
    case NOTIFICATION_KIND.STAFF_PHONE_MISSING:
    case NOTIFICATION_KIND.SYSTEM_TEST:
      return NOTIFICATION_TYPE.SYSTEM;
  }

  // Bilinmeyen kind → 'system' (fırlatma yok; tür yalnız UI sınıflandırması içindir).
  return NOTIFICATION_TYPE.SYSTEM;
}

/**
 * Bildirim kategorisi. Yan menü/filtre/rozet rengi için tek sınıflandırma.
 * Yeni kind eklerken buraya da ekle (ya da önek kuralıyla eşleşir).
 */
export const NOTIFICATION_CATEGORY = {
  ORDER: 'order',
  APPROVAL: 'approval',
  STOCK: 'stock',
  TRIP: 'trip',
  CASH: 'cash',
  REPORT: 'report',
  STAFF: 'staff',
  SYSTEM: 'system',
} as const;

export type NotificationCategory =
  (typeof NOTIFICATION_CATEGORY)[keyof typeof NOTIFICATION_CATEGORY];

/**
 * Günlük rapor saat eşiği (Europe/Istanbul). DailyReportNotifier bu saate
 * ulaşınca tetiklenir; kullanıcı bunu dışarıdan değiştiremez (MVP).
 */
export const DAILY_REPORT_HOUR = 19;
/** Pazartesi haftalık karne saati (İstanbul, O6). */
export const WEEKLY_SCORECARD_HOUR = 8;
/** Günlük sağlık raporu saati (İstanbul). */
export const HEALTH_REPORT_HOUR = 8;

/**
 * Kind → kategori eşlemesi.
 *
 * - Eski kind'lar için AÇIK tablo: belirsizlik durumunda "system" düşer; yeni
 *   kind'lar önek kuralıyla (`order.*`, `trip.*`, …) eşleşir.
 * - `order.transfer.*` onay mekanizmasının parçası (talep/red/onay); APPROVAL altında
 *   toplanır (RELOAD_REQUEST, PIN_RESET ile aynı kategori).
 * - Bilinmeyen kind → 'system'. Kategori yalnız filtre/rozet içindir; hata
 *   fırlatmaz.
 */
export function kindCategory(kind: string): NotificationCategory {
  switch (kind) {
    // ── Eski — açık eşleme (önek kuralı tutmaz) ──
    case NOTIFICATION_KIND.ORDER_RECEIVED:
    case NOTIFICATION_KIND.ORDER_ASSIGNED:
    case NOTIFICATION_KIND.ORDER_UNASSIGNED:
    case NOTIFICATION_KIND.ORDER_ASSIGNED_UNSTAFFED:
    case NOTIFICATION_KIND.ORDER_CANCELLED:
    case NOTIFICATION_KIND.ORDER_CLOSED_EXTERNALLY:
    case NOTIFICATION_KIND.HAYAT_CLOSED_EXTERNALLY:
    case NOTIFICATION_KIND.ABANT_CLOSED_EXTERNALLY:
    case NOTIFICATION_KIND.LATE_ORDER_WINDOW:
      return NOTIFICATION_CATEGORY.ORDER;

    case NOTIFICATION_KIND.ORDER_TRANSFER_REQUESTED:
    case NOTIFICATION_KIND.ORDER_TRANSFER_REJECTED:
    case NOTIFICATION_KIND.ORDER_TRANSFER_APPROVED:
    case NOTIFICATION_KIND.RELOAD_REQUEST:
    case NOTIFICATION_KIND.RELOAD_APPROVED:
    case NOTIFICATION_KIND.RELOAD_REJECTED:
    case NOTIFICATION_KIND.DELIVERY_DEVIATION:
    case NOTIFICATION_KIND.DELIVERY_DEVIATION_REVIEWED:
    case NOTIFICATION_KIND.DELIVERY_DEVIATION_REJECTED:
    case NOTIFICATION_KIND.PIN_RESET_REQUEST:
      return NOTIFICATION_CATEGORY.APPROVAL;

    case NOTIFICATION_KIND.STOCK_SHIPMENT_RECEIVED:
    case NOTIFICATION_KIND.STOCK_ADJUSTED:
    case NOTIFICATION_KIND.STOCK_FOREIGN_RECEIVED:
    case NOTIFICATION_KIND.STOCK_FOREIGN_SETTLED:
    case NOTIFICATION_KIND.STOCK_COUNT_DIFF:
      // PRICE_SYNC tasarımda "mevcut (değişmez) yöneticilere" listeleniyor;
      // envanter fiyat senkronizasyonu stok işlemidir (scheduler.service.ts:423).
      return NOTIFICATION_CATEGORY.STOCK;
    case NOTIFICATION_KIND.PRICE_SYNC:
      return NOTIFICATION_CATEGORY.STOCK;

    case NOTIFICATION_KIND.TRIP_LOADED:
    case NOTIFICATION_KIND.TRIP_CLOSED:
    case NOTIFICATION_KIND.TRIP_VARIANCE:
    case NOTIFICATION_KIND.TRIP_VOIDED:
    case NOTIFICATION_KIND.DELIVERY_DROPPED_DUPLICATE:
    case NOTIFICATION_KIND.DELIVERY_PHOTO_MISSING:
      return NOTIFICATION_CATEGORY.TRIP;

    case NOTIFICATION_KIND.CASH_HANDOVER_CONFIRMED:
      return NOTIFICATION_CATEGORY.CASH;

    case NOTIFICATION_KIND.REPORT_DAILY_READY:
    case NOTIFICATION_KIND.REPORT_WEEKLY_SCORECARD:
    case NOTIFICATION_KIND.REPORT_HEALTH_DAILY:
      return NOTIFICATION_CATEGORY.REPORT;

    case NOTIFICATION_KIND.STAFF_PHONE_MISSING:
      return NOTIFICATION_CATEGORY.STAFF;

    case NOTIFICATION_KIND.HAYAT_OBSERVED_CONFLICT:
    case NOTIFICATION_KIND.HAYAT_RETRY_LIMIT:
    case NOTIFICATION_KIND.HAYAT_PUSH_SENT:
    case NOTIFICATION_KIND.HAYAT_PUSH_FAILED:
    case NOTIFICATION_KIND.CHANNEL_WRITE_ALERT:
    case NOTIFICATION_KIND.OWNER_MESSAGE:
      return NOTIFICATION_CATEGORY.SYSTEM;

    // Kanal oturumu uyarıları: entegrasyon tarafı, sistem kategorisinde.
    case NOTIFICATION_KIND.CHANNEL_SESSION_EXPIRING:
    case NOTIFICATION_KIND.CHANNEL_SESSION_EXPIRED:
      return NOTIFICATION_CATEGORY.SYSTEM;
  }

  // ── Yeni kind'lar — önek kuralı (önek burada sabit sözlükle eşleşir) ──
  if (kind.startsWith('order.')) return NOTIFICATION_CATEGORY.ORDER;
  if (kind.startsWith('reload.') || kind.startsWith('approval.')) {
    return NOTIFICATION_CATEGORY.APPROVAL;
  }
  if (kind.startsWith('stock.')) return NOTIFICATION_CATEGORY.STOCK;
  if (kind.startsWith('trip.')) return NOTIFICATION_CATEGORY.TRIP;
  if (kind.startsWith('cash.') || kind.startsWith('handover.')) {
    return NOTIFICATION_CATEGORY.CASH;
  }
  if (kind.startsWith('report.')) return NOTIFICATION_CATEGORY.REPORT;
  if (kind.startsWith('staff.')) return NOTIFICATION_CATEGORY.STAFF;

  // Bilinmeyen → 'system' (fırlatma yok; kategori yalnız UI filtre/rozet içindir).
  return NOTIFICATION_CATEGORY.SYSTEM;
}

// ──────────────────────────────────────────────────────────────────────────────
// Seviye → kanal politikası (N1)
// ──────────────────────────────────────────────────────────────────────────────

/** `Notification.level` (Prisma enum `NotificationLevel` ile bire bir). */
export type NotificationLevelName = 'CRITICAL' | 'WARNING' | 'INFO' | 'OPERATION' | 'SUCCESS';

export type NotificationSound = 'urgent' | 'default' | 'silent';

export interface NotificationPolicy {
  /** Telefona push gider mi? false = yalnız uygulama içi liste. */
  push: boolean;
  sound: NotificationSound;
  /** Sessiz saatte de gönderilir mi? Yalnız CRITICAL. */
  quietExempt: boolean;
}

/**
 * Seviyesi push gerektirmese bile telefona gitmesi gereken türler: biri bir şey bekliyor ya da
 * kişiden kişiye yazılmış mesaj. Yeni kind eklerken buraya bilinçli eklenir.
 */
const PUSH_BY_KIND: ReadonlySet<string> = new Set([
  'driver.message',
  NOTIFICATION_KIND.OWNER_MESSAGE,
  NOTIFICATION_KIND.RELOAD_APPROVED,
  NOTIFICATION_KIND.DIGEST_MORNING,
  // Test bildirimi (panel/mobil "kendime test bildirimi gönder" butonu). level INFO olsa
  // da OS bildirimi GÖSTERİLMELİ: aksi hâlde tier'ı "info" düşer, mobilde "bilgi"
  // anahtarı varsayılan KAPALI olduğu için buton sessizce hiçbir şey yapmış gibi görünür.
  NOTIFICATION_KIND.SYSTEM_TEST,
  // Onay sonuçları ve eşleştirme talebi: biri cevap bekliyor / sonucu bekliyor.
  'DEVICE_PAIRING_REQUESTED',
  NOTIFICATION_KIND.ORDER_TRANSFER_APPROVED,
  NOTIFICATION_KIND.ORDER_TRANSFER_REJECTED,
]);

/**
 * Tekilleştirilen (10 dk) TEK tür listesi (N1). Yalnız aynı hedef için tekrar tekrar üretilen
 * alarm/durum gözlemleri girer (scraper her turda aynı uyarıyı yazabilir). Onay talepleri
 * (RELOAD_REQUEST, PIN_RESET_REQUEST, pairing), onay sonuçları, sipariş/sefer geçişleri
 * (order.assigned, trip.loaded …) ve mesajlar ASLA girmez: her satır ayrı bir olaydır.
 * Yeni alarm türü eklenirse buraya bilinçli eklenir.
 */
export const DEDUPE_KINDS: ReadonlySet<string> = new Set([
  'DEVICE_IN_USE_ATTEMPT',
  'DEVICE_LOGIN_LOCKED',
  'getir.closedOrdersOverflow',
  NOTIFICATION_KIND.PRICE_SYNC,
  NOTIFICATION_KIND.LATE_ORDER_WINDOW,
  NOTIFICATION_KIND.HAYAT_CLOSED_EXTERNALLY,
  NOTIFICATION_KIND.ABANT_CLOSED_EXTERNALLY,
  'getir.closedExternally',
  NOTIFICATION_KIND.ORDER_CLOSED_EXTERNALLY,
  'hayat.cancelledAfterClose',
  'abant.cancelledAfterClose',
]);

/**
 * Tek saf politika: seviye (+ nadiren tür) -> kanal, ses, sessiz saat muafiyeti.
 *
 *  - CRITICAL            -> push + `urgent` ses + sessiz saatten MUAF.
 *  - WARNING / OPERATION -> push + `default` ses (onay/eylem gerektiren).
 *  - INFO / SUCCESS      -> push YOK, yalnız uygulama içi; `PUSH_BY_KIND` türleri hariç.
 *
 * `quietExempt` seviyeden başka bir şeye bağlanmaz: süpürücü sessiz saatte yalnız CRITICAL satırı
 * sorgular, tür istisnası bu varsayımı bozmasın.
 */
export function notificationPolicy(level: string, kind: string): NotificationPolicy {
  if (level === 'CRITICAL') return { push: true, sound: 'urgent', quietExempt: true };
  if (level === 'WARNING' || level === 'OPERATION' || PUSH_BY_KIND.has(kind)) {
    return { push: true, sound: 'default', quietExempt: false };
  }
  return { push: false, sound: 'silent', quietExempt: false };
}

// ──────────────────────────────────────────────────────────────────────────────
// Seviye → tier (S-N) — istemci ayar anahtarları bu üç seviyeye bakar
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Bildirimin aciliyeti (3 seviye). Cihaz ayarları bu seviyeye bakar:
 * `urgent`+`action` → OS bildirimi, `info` → uygulama içi liste.
 */
export type NotificationTier = 'urgent' | 'action' | 'info';

/**
 * `level` + `kind` → tier.
 *
 *  - `CRITICAL` → `urgent` (sessiz saatten muaf, her zaman bildirilir).
 *  - `notificationPolicy(level, kind).push === true` → `action` (bir şey bekliyor:
 *    onay, eylem, kişisel mesaj).
 *  - Kalan → `info` (yalnız uygulama içinde görünür).
 *
 * Tier SEVİYEDEN değil POLİKİDAN türer: `PUSH_BY_KIND` istisnaları (kişisel mesaj, onay
 * sonucu, eşleştirme talebi) `push` alır ama `WARNING/OPERATION` seviyesinde değildir;
 * seviyeden türetilseydi bu türler "bilgi"ye düşerdi.
 */
export function notificationTier(level: string, kind: string): NotificationTier {
  if (level === 'CRITICAL') return 'urgent';
  if (notificationPolicy(level, kind).push === true) return 'action';
  return 'info';
}

// ──────────────────────────────────────────────────────────────────────────────
// Sessiz saat (N1)
// ──────────────────────────────────────────────────────────────────────────────

/** `HH:mm`, 24 saat. `start === end` = sessiz saat kapalı. `start > end` = gece yarısını geçer. */
export interface QuietHours {
  start: string;
  end: string;
}

export const DEFAULT_QUIET_HOURS: QuietHours = { start: '22:00', end: '07:00' };

export const QUIET_HOUR_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** `HH:mm` -> gün içi dakika (0-1439); geçersizse NaN. */
export function quietMinutes(hhmm: string): number {
  if (!QUIET_HOUR_PATTERN.test(hhmm)) return Number.NaN;
  return Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
}

const minuteFormatters = new Map<string, Intl.DateTimeFormat>();

/** Verilen anın `timeZone`'daki gün içi dakikası (0-1439). */
export function localMinuteOfDay(at: Date, timeZone: string = ISTANBUL_TZ): number {
  let f = minuteFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    minuteFormatters.set(timeZone, f);
  }
  const parts = f.formatToParts(at);
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24;
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return h * 60 + m;
}

/** `start` dahil, `end` hariç: 22:00 sessiz, 07:00 değil. Geçersiz/eşit sınır -> false. */
export function isQuietMinute(minute: number, quiet: QuietHours): boolean {
  const s = quietMinutes(quiet.start);
  const e = quietMinutes(quiet.end);
  if (Number.isNaN(s) || Number.isNaN(e) || s === e) return false;
  return s < e ? minute >= s && minute < e : minute >= s || minute < e;
}

export function isQuietAt(at: Date, quiet: QuietHours, timeZone: string = ISTANBUL_TZ): boolean {
  return isQuietMinute(localMinuteOfDay(at, timeZone), quiet);
}

export interface QuietWindow {
  /** Pencere başlangıcı (dahil). */
  start: Date;
  /** Pencere bitişi (hariç). */
  end: Date;
  /** Bitişin yerel günü `YYYY-MM-DD` — sabah özetinin tekillik anahtarı. */
  day: string;
}

/**
 * `at` sessiz saat DIŞINDAYKEN en son biten sessiz pencere. Sessiz saat kapalı/geçersizse ya da
 * `at` hâlâ sessizse null. Sabit UTC kayması varsayar (Türkiye'de yaz saati yok); dakika
 * hassasiyetindedir.
 */
export function lastQuietWindow(
  at: Date,
  quiet: QuietHours,
  timeZone: string = ISTANBUL_TZ,
): QuietWindow | null {
  const s = quietMinutes(quiet.start);
  const e = quietMinutes(quiet.end);
  if (Number.isNaN(s) || Number.isNaN(e) || s === e) return null;
  const nowMinute = localMinuteOfDay(at, timeZone);
  if (isQuietMinute(nowMinute, quiet)) return null;
  const sinceEnd = (nowMinute - e + 1440) % 1440;
  const length = (e - s + 1440) % 1440;
  const floored = Math.floor(at.getTime() / 60_000) * 60_000;
  const end = new Date(floored - sinceEnd * 60_000);
  return {
    start: new Date(end.getTime() - length * 60_000),
    end,
    day: istanbulDayString(end, timeZone),
  };
}

/**
 * `at` sessiz saatin İÇİNDEYKEN içinde bulunulan pencerenin başlangıcı; değilse (ya da kapalıysa)
 * null. Dakika hassasiyetinde. Sağlık raporu "ölü push" sayımı bununla bekleyenleri ayırır.
 */
export function currentQuietStart(
  at: Date,
  quiet: QuietHours,
  timeZone: string = ISTANBUL_TZ,
): Date | null {
  const s = quietMinutes(quiet.start);
  const e = quietMinutes(quiet.end);
  if (Number.isNaN(s) || Number.isNaN(e) || s === e) return null;
  const nowMinute = localMinuteOfDay(at, timeZone);
  if (!isQuietMinute(nowMinute, quiet)) return null;
  const sinceStart = (nowMinute - s + 1440) % 1440;
  return new Date(Math.floor(at.getTime() / 60_000) * 60_000 - sinceStart * 60_000);
}

/**
 * Kanala yazma: iş başına "engellendi" uyarısı üreten BLOKE nedenleri (Y1b). Yapılandırma/oturum
 * nedenleri (yazıcı yok, yetki kapalı, çekim durdu, oturum) iş başına uyarı DEĞİLDİR: yazma bayrağı açılırken
 * her iş için ayrı satır yağmasın; kaynak düzeyindeki oturum uyarısı ayrıdır. 19:00 raporundaki
 * "durdu/engellendi" sayısı da aynı listeyi kullanır.
 */
export const CHANNEL_WRITE_ALERT_BLOCKED_CODES = ['tenant-restricted'] as const;
